/**
 * OrganizationRow Component
 *
 * Single row displaying organization summary information
 */

import React from 'react';
import {
  Business as OrgIcon,
  ChevronRight as ChevronIcon,
  Description as NotesIcon,
  People as ProvidersIcon,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { OrganizationSummary } from '../../types';
import { FlagBadge } from '../shared/FlagBadge';
import { QualityScore } from '../shared/QualityScore';

interface OrganizationRowProps {
  /** Organization data */
  organization: OrganizationSummary;
  /** Click handler */
  onClick?: (organizationId: string) => void;
}

export const OrganizationRow: React.FC<OrganizationRowProps> = ({ organization, onClick }) => {
  const theme = useTheme();

  const handleClick = () => {
    onClick?.(organization.organization_id);
  };

  const displayName = organization.organization_name || organization.organization_id.slice(0, 8);
  const compliancePct = organization.avg_compliance_pct;
  const cptMatchRate = organization.cpt_match_rate;
  const criticalFlags = organization.notes_with_critical_flags || 0;

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        borderBottom: `1px solid ${theme.palette.grey[100]}`,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background-color 0.15s ease',
        '&:hover': onClick
          ? {
              bgcolor: theme.palette.grey[50],
            }
          : {},
        '&:last-child': {
          borderBottom: 'none',
        },
      }}
    >
      {/* Organization Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 44,
          height: 44,
          borderRadius: '50%',
          bgcolor: theme.palette.primary.light + '20',
          color: theme.palette.primary.main,
          flexShrink: 0,
        }}
      >
        <OrgIcon />
      </Box>

      {/* Organization Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayName}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ProvidersIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {organization.total_providers || 0} providers
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <NotesIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {organization.total_notes_audited || 0} notes
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Quality Score */}
      <Box sx={{ textAlign: 'center', minWidth: 60 }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 0.25 }}>
          Quality
        </Typography>
        <QualityScore score={organization.avg_quality_score} size="small" />
      </Box>

      {/* Compliance */}
      <Box sx={{ textAlign: 'center', minWidth: 80 }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 0.25 }}>
          Compliance
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color:
              compliancePct !== null && compliancePct !== undefined
                ? compliancePct >= 80
                  ? theme.palette.success.main
                  : compliancePct >= 60
                    ? theme.palette.warning.main
                    : theme.palette.error.main
                : theme.palette.text.disabled,
          }}
        >
          {compliancePct !== null && compliancePct !== undefined
            ? `${compliancePct.toFixed(0)}%`
            : '—'}
        </Typography>
      </Box>

      {/* CPT Match Rate */}
      <Box sx={{ textAlign: 'center', minWidth: 80 }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 0.25 }}>
          CPT Match
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color:
              cptMatchRate !== null && cptMatchRate !== undefined
                ? cptMatchRate >= 90
                  ? theme.palette.success.main
                  : cptMatchRate >= 70
                    ? theme.palette.warning.main
                    : theme.palette.error.main
                : theme.palette.text.disabled,
          }}
        >
          {cptMatchRate !== null && cptMatchRate !== undefined
            ? `${cptMatchRate.toFixed(0)}%`
            : '—'}
        </Typography>
      </Box>

      {/* Flags */}
      <Box sx={{ display: 'flex', gap: 0.75, minWidth: 100, justifyContent: 'flex-end' }}>
        {criticalFlags > 0 ? (
          <FlagBadge priority="Critical" count={criticalFlags} size="small" />
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.success.main, fontWeight: 500 }}>
            No critical
          </Typography>
        )}
      </Box>

      {/* Chevron */}
      {onClick && <ChevronIcon sx={{ color: theme.palette.grey[400], flexShrink: 0 }} />}
    </Box>
  );
};

export default OrganizationRow;

